import React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import { CompanyDetails } from "./components/Summary/CompanyDetails";
import { CompanyDescription } from "./components/Summary/CompanyDescription";
import { GlobalHeadquarters } from "./components/Summary/GlobalHeadquarters";
import { useCompanyPageStore } from "../../../store/companyPageStore";
import { CompanyCountByCountryChart } from "./components/Summary/CompanyCountByCountryChart";

export const SummaryViewSkeleton = () => {
  return (
    <Box>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          {/* Company Details Skeleton */}
          <Box>
            <Skeleton variant="text" width={200} height={32} sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              {[1, 2, 3].map((col) => (
                <Grid key={col} size={4}>
                  {[1, 2, 3, 4].map((item) => (
                    <Box key={item} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width={80} height={20} />
                      <Skeleton variant="text" width="100%" height={16} />
                    </Box>
                  ))}
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 12, lg: 5 }}>
          {/* Company Description Skeleton */}
          <Box>
            <Skeleton variant="text" width={250} height={32} sx={{ mb: 3 }} />
            <Box>
              <Skeleton variant="rectangular" width="100%" height={300} />
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          {/* Global Headquarters Skeleton */}
          <Box>
            <Skeleton variant="text" width={280} height={32} sx={{ mb: 3 }} />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={300}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          {/* Company Count by Country Chart Skeleton */}
          <Box>
            <Skeleton variant="text" width={300} height={32} sx={{ mb: 3 }} />
            <Skeleton variant="rectangular" width="100%" height={300} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export const SummaryView = React.memo(() => {
  const company = useCompanyPageStore((state) => state.company);
  const companyLoading = useCompanyPageStore(
    (state) => state.loadingStates.company
  );

  if (companyLoading || !company) {
    return <SummaryViewSkeleton />;
  }

  const hasLocation = company.latitude && company.longitude;
  const { description } = company;
  const shouldShowDescription = hasLocation || description;

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid
          size={{
            xs: 12,
            md: shouldShowDescription ? 6 : 12,
            lg: shouldShowDescription ? 4 : 6,
          }}
        >
          <CompanyDetails />
        </Grid>

        {shouldShowDescription && (
          <Grid size={{ xs: 12, md: 12, lg: 5 }}>
            <CompanyDescription />
          </Grid>
        )}

        <Grid
          size={{
            xs: 12,
            md: shouldShowDescription ? 6 : 12,
            lg: shouldShowDescription ? 3 : 6,
          }}
        >
          <GlobalHeadquarters />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <CompanyCountByCountryChart />
        </Grid>
      </Grid>
    </Box>
  );
});

SummaryView.displayName = "SummaryView";
